import React, { FC, useContext, useState } from 'react';

import CircleCheckFill from '@gravity-ui/icons/CircleCheckFill';
import CircleExclamation from '@gravity-ui/icons/CircleExclamation';
import Clock from '@gravity-ui/icons/Clock';
import ShieldCheck from '@gravity-ui/icons/ShieldCheck';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button, CardTheme, Icon, Label, LabelProps, Modal, TextProps } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { DraftStatus } from 'client/model/draft';
import { OperationType } from 'client/model/operation';
import { getIsAdmin } from 'client/utils';
import * as keyset from 'i18n/DraftsStatuses';

import styles from './OperationStatus.module.scss';

const t = i18n(keyset);

export const STATUS_TO_THEME_MAP: Record<
    OperationType | DraftStatus,
    { color: TextProps['color']; theme: CardTheme; icon: React.JSX.Element | undefined; label: string } | undefined
> = {
    skillCreated: undefined,
    skillWithdrawn: {
        label: t('installable-app-status-withdraw'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },
    skillStopped: {
        label: t('installable-app-status-stopped'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    skillDeleted: {
        label: t('installable-app-status-deleted'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    reviewRequested: {
        label: t('installable-app-status-review-request'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },
    reviewCancelled: {
        label: t('installable-app-status-review-canceled'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    reviewApproved: {
        label: t('installable-app-status-review-approved'),
        color: 'info-heavy',
        theme: 'info',
        icon: <ShieldCheck />,
    },
    deployRequested: {
        label: t('installable-app-status-deploy-request'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },
    deployCompleted: {
        label: t('installable-app-status-published'),
        color: 'positive-heavy',
        theme: 'success',
        icon: <CircleCheckFill />,
    },
    deployRejected: {
        label: t('installable-app-status-deploy-rejected'),
        color: 'danger-heavy',
        theme: 'danger',
        icon: <CircleExclamation />,
    },
    settingsReviewApproved: {
        label: t('installable-app-status-settings-review-approved'),
        color: 'info-heavy',
        theme: 'info',
        icon: <ShieldCheck />,
    },
    deviceTestPrepared: {
        label: t('installable-app-status-device-test-prepared'),
        color: 'info-heavy',
        theme: 'info',
        icon: <Clock />,
    },
    deviceTestStarted: {
        label: t('installable-app-status-device-test-started'),
        color: 'info-heavy',
        theme: 'info',
        icon: <Clock />,
    },
    deviceTestCompleted: {
        label: t('installable-app-status-device-test-completed'),
        color: 'info-heavy',
        theme: 'success',
        icon: <CircleCheckFill />,
    },
    [DraftStatus.AppScanRequested]: {
        label: t('scanning'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },
    [DraftStatus.AppScanRequestedAfterModeration]: {
        label: t('scanning'),
        color: 'warning-heavy',
        theme: 'warning',
        icon: <Clock />,
    },

    reviewCancelledByUser: undefined,
    inDevelopment: undefined,
    skillNominated: undefined,
    skillNominationRotationCompleted: undefined,
    skillNominationRotationWithdrawn: undefined,
    [DraftStatus.ReviewRejected]: undefined,
    [DraftStatus.TestRequired]: undefined,
    [DraftStatus.MeetingApproval]: undefined,
    [DraftStatus.WaitingForTesting]: undefined,
    [DraftStatus.DevicesTesting]: undefined,
    [DraftStatus.DevicesTestingCompleted]: undefined,
};

const MAP_THEME_TO_ICON_STYLE: Record<Extract<LabelProps['theme'], string>, string> = {
    clear: styles.operationStatusIcon_clear,
    normal: styles.operationStatusIcon_clear,
    utility: styles.operationStatusIcon_clear,
    unknown: styles.operationStatusIcon_clear,
    danger: styles.operationStatusIcon_danger,
    info: styles.operationStatusIcon_info,
    success: styles.operationStatusIcon_success,
    warning: styles.operationStatusIcon_warning,
};

const DRAFT_STATUSES_OVERRIDE = [OperationType.ReviewCancelled, OperationType.DeployRejected];
const OPERATIONS_DISPLAYED_FOR_PUBLISHED_SKILL = [OperationType.SkillWithdrawn, OperationType.SkillStopped];

export const getDisplayDraftStatus = (
    isDraft: boolean,
    isOnAir: boolean,
    draftStatus: DraftStatus,
    lastOperationType: OperationType,
) => {
    if (
        !getIsAdmin() &&
        [DraftStatus.AppScanRequested, DraftStatus.AppScanRequestedAfterModeration].includes(draftStatus)
    ) {
        return DraftStatus.ReviewRequested;
    }

    if (lastOperationType === OperationType.SkillDeleted) return lastOperationType;
    if (isOnAir && !isDraft) {
        return OPERATIONS_DISPLAYED_FOR_PUBLISHED_SKILL.includes(lastOperationType)
            ? lastOperationType
            : OperationType.DeployCompleted;
    } else {
        if (draftStatus === DraftStatus.InDevelopment && DRAFT_STATUSES_OVERRIDE.includes(lastOperationType)) {
            return lastOperationType;
        }
        return draftStatus as DraftStatus;
    }
};

export const OperationStatus: FC<{ isDraft: boolean }> = ({ isDraft }) => {
    const [openModal, setOpenModal] = useState(false);
    const appContext = useContext(CurrentAliceAppContext);

    if (!appContext) return null;

    const { aliceApp, lastOperationType = OperationType.SkillCreated, lastOperationComment } = appContext;

    const displayOperationType: OperationType | DraftStatus = getDisplayDraftStatus(
        isDraft,
        aliceApp.onAir,
        aliceApp.draft.status,
        lastOperationType,
    );
    const operationIcon = STATUS_TO_THEME_MAP[displayOperationType]?.['icon'] || <CircleExclamation />;

    const isReviewCanceled =
        aliceApp.draft.status === DraftStatus.InDevelopment && lastOperationType === OperationType.ReviewCancelled;
    const isReviewApproved =
        aliceApp.draft.status === DraftStatus.ReviewApproved && lastOperationType === OperationType.ReviewApproved;
    let displayLastOperationComment = '';
    if (lastOperationComment && (isReviewApproved || isReviewCanceled)) {
        displayLastOperationComment = lastOperationComment;
    }

    const handleLabelClick = displayLastOperationComment ? () => setOpenModal(true) : undefined;

    const operationTheme = STATUS_TO_THEME_MAP[displayOperationType]?.['theme'];
    const operationLabel = STATUS_TO_THEME_MAP[displayOperationType]?.['label'];

    if (!operationTheme) return null;

    return (
        <div className={styles.operationStatusContainer}>
            <span className={styles.operationDesktopStyling}>{' • '}</span>
            <Label
                className={styles.operationStatusLabel}
                theme={operationTheme}
                icon={operationIcon}
                onClick={handleLabelClick}
                size="m"
            >
                <span className={styles.operationDesktopStyling}>{operationLabel}</span>
            </Label>
            <div
                className={clx(styles.operationStatusIcon, MAP_THEME_TO_ICON_STYLE[operationTheme])}
                onClick={handleLabelClick}
            >
                {operationIcon}
            </div>
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <div className={styles.operationModal__container}>
                    <header className={styles.operationModal__header}>
                        <div className={styles.operationModal__headerContent}>
                            {operationIcon}
                            {operationLabel}
                        </div>
                        <Button
                            className={styles.operationModal__closeButton}
                            onClick={() => setOpenModal(false)}
                            view="flat"
                            size="l"
                        >
                            <Icon data={Xmark} />
                        </Button>
                    </header>
                    <div className={styles.operationModal__content}>{displayLastOperationComment}</div>
                    <footer className={styles.operationModal__footer}>
                        <Button onClick={() => setOpenModal(false)} view="action">
                            {t('it-is-ok')}
                        </Button>
                    </footer>
                </div>
            </Modal>
        </div>
    );
};
